import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LLink as Link } from '../components/LocalizedLink';
import { Eye, LogIn, UserPlus, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      navigate(u.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Login failed');
    } finally { setBusy(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await register(registerForm.name, registerForm.email, registerForm.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Register failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f7fafd] py-16">
      <div className="container-x max-w-md mx-auto">
        <div className="flex flex-col items-center mb-6">
          <span className="w-12 h-12 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
            <Eye size={20} />
          </span>
          <h1 className="mt-4 text-2xl font-bold text-[#0b1f3a]">Welcome back</h1>
          <p className="text-slate-600 text-sm mt-1">Manage your Spanish insurance in one place.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
          {/* Toggle pills */}
          <div className="grid grid-cols-2 mb-5 bg-slate-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); }}
              className={`h-9 rounded-full text-sm font-semibold transition-colors ${
                mode === 'login' ? 'bg-white text-[#0d3a8a] shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
              data-testid="toggle-login"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => { setMode('register'); setError(''); }}
              className={`h-9 rounded-full text-sm font-semibold transition-colors ${
                mode === 'register' ? 'bg-white text-[#0d3a8a] shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
              data-testid="toggle-register"
            >
              Create account
            </button>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
              <AlertCircle size={16} className="mt-0.5" /> {error}
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label className="field-label">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  data-testid="login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="field-label !mb-0">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-[#0d3a8a] font-semibold hover:underline"
                    data-testid="login-forgot-password"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  data-testid="login-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center disabled:opacity-60"
                data-testid="login-submit"
              >
                <LogIn size={16} /> {busy ? 'Please wait...' : 'Sign in'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div>
                <Label className="field-label">Full name</Label>
                <Input
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  required
                  data-testid="register-name"
                />
              </div>
              <div>
                <Label className="field-label">Email</Label>
                <Input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  required
                  data-testid="register-email"
                />
              </div>
              <div>
                <Label className="field-label">Password (min 6 chars)</Label>
                <Input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center disabled:opacity-60"
                data-testid="register-submit"
              >
                <UserPlus size={16} /> {busy ? 'Please wait...' : 'Create account'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Back to <Link to="/" className="text-[#0d3a8a] font-semibold">homepage</Link>
        </p>
      </div>
    </div>
  );
}
